// File: src/pages/TargetReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Label,
  Button,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Eye } from "react-feather";
import { ImDownload } from "react-icons/im";
import {
  FaFilter,
  FaArrowAltCircleRight,
  FaArrowAltCircleLeft,
} from "react-icons/fa";
import {
  BsFillArrowUpSquareFill,
  BsFillArrowDownSquareFill,
} from "react-icons/bs";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import swal from "sweetalert";

// === Everything below must come from ../import/ per your instructions ===
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { _Get, _Post } from "../import/ApiEndPoint/ApiCalling";
import {
  Create_Account_List,
  SalesPerson_targetReport,
} from "../import/ApiEndPoint/Api";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";
// ======================================================================

// import SalesPersonTarget from "./SalesPersonTarget";

// ---- inline page CSS (no SCSS) ----
const injectOnce = () => {
  if (document.getElementById("target-inline-css")) return;
  const s = document.createElement("style");
  s.id = "target-inline-css";
  s.textContent = `
    .tw-card{border:1px solid #e5e7eb;border-radius:14px;background:#fff;}
    .tw-toolbar{display:flex;gap:12px;flex-wrap:wrap;align-items:center;padding:8px 12px;background:#f4f7f6;border:1px solid #e5e7eb;border-radius:14px;}
    .count-pill{background:#eef2ff;color:#3730a3;font-weight:700;padding:6px 10px;border-radius:999px;}
    .tw-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:14px;background:#fff;}
    .tw-table{width:100%;border-collapse:collapse;table-layout:auto;min-width:720px;}
    .tw-table thead th{background:#f4f7f6;color:#0f172a;font-weight:700;text-transform:uppercase;font-size:12px;letter-spacing:.03em;border-bottom:1px solid #e5e7eb;padding:8px 10px;position:sticky;top:0;z-index:1;text-align:center;white-space:nowrap;}
    .tw-table tbody td{padding:8px 10px;border-bottom:1px solid #f1f5f9;vertical-align:middle;color:#0f172a;font-size:14px;text-align:center;}
    .tw-table tbody tr:hover{background:#f9fbff;}
    .link{color:#1d4ed8;text-decoration:underline;cursor:pointer}
    .picklist{border:1px solid #e5e7eb;border-radius:10px;background:#fff;padding:8px;max-height:300px;overflow:auto;}
    .pickitem{padding:8px 10px;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:6px;background:#fff;cursor:pointer;}
    .dl-menu{position:absolute;right:0;top:32px;z-index:5;border:1px solid rgb(8,91,245);background:#fff;border-radius:10px;overflow:hidden;min-width:130px;box-shadow:0 12px 24px rgba(0,0,0,.12);}
    .dl-item{padding:10px 12px;cursor:pointer;font-weight:600;letter-spacing:.02em;}
    .dl-item:hover{background:#f3f4f6;}
  `;
  document.head.appendChild(s);
};

// small helpers
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const BASE_COLS = [
  {
    key: "month",
    header: "Month",
    get: (r) => {
      const m = new Date(r?.createdAt).getMonth() + 1;
      return Number.isFinite(m) ? m : "-";
    },
  },
  {
    key: "view",
    header: "View Target",
    render: (r, onView) => (
      <span className="link" title="View Target" onClick={() => onView(r)}>
        <Eye size={16} style={{ verticalAlign: "middle" }} />
      </span>
    ),
  },
  {
    key: "salesPerson",
    header: "Sales Person Name",
    get: (r) => `${toStr(r?.firstName)} ${toStr(r?.lastName)}`.trim() || "-",
  },
];

export default function TargetReport() {
  const navigate = useNavigate();
  const bufferRef = useRef([]);
  const [loading, setLoading] = useState(true);
  const [masterShow, setMasterShow] = useState(false);
  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [isOpenDl, setIsOpenDl] = useState(false);
  const [modalCols, setModalCols] = useState(false);

  // column chooser state
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // view target modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // permissions + master
        const user = JSON.parse(localStorage.getItem("userData") || "{}");
        if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
        const p = CheckPermission("Sales Report and Amount");
        if (p && typeof p === "object")
          setPermissions((old) => ({ ...old, ...p }));

        // fetch list of sales persons
        const url = `${Create_Account_List + user?._id}/`;
        const res = await _Get(url, user?.database);
        const salesPersons = (res?.adminDetails || []).filter((e) =>
          (e?.rolename?.roleName || "").toLowerCase().includes("sales person")
        );

        setAllRows(salesPersons);
        setRows(salesPersons);

        // restore columns order/selection
        const saved = localStorage.getItem("OrderReportList");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const keys = Array.isArray(parsed)
              ? parsed.map((c) =>
                  c.headerName
                    ? BASE_COLS.find((b) => b.header === c.headerName)?.key
                    : c?.key
                )
              : null;
            if (keys && keys.filter(Boolean).length) {
              setSelectedKeys(keys.filter(Boolean));
            } else {
              setSelectedKeys(BASE_COLS.map((c) => c.key));
            }
          } catch {
            setSelectedKeys(BASE_COLS.map((c) => c.key));
          }
        } else {
          setSelectedKeys(BASE_COLS.map((c) => c.key));
        }
      } catch (e) {
        console.error("[TargetReport] init error:", e);
        setAllRows([]);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const colMap = useMemo(() => {
    const m = new Map();
    BASE_COLS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);
  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  // filters
  const filteredRows = useMemo(() => {
    let data = allRows;

    if (startDate && endDate) {
      data = data.filter((r) => {
        const d = r?.updatedAt || r?.createdAt;
        if (!d) return false;
        const only = String(d).split("T")[0];
        return only >= startDate && only <= endDate;
      });
    }

    const term = search.trim().toLowerCase();
    if (!term) return data;

    return data.filter((r) => {
      const hay = [r?.firstName, r?.lastName, r?.email, r?.mobileNumber]
        .map(toStr)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [allRows, startDate, endDate, search]);

  // column chooser handlers
  const openCols = () => {
    bufferRef.current = [];
    setSelectedIndex(-1);
    setModalCols(true);
  };
  const closeCols = () => setModalCols(false);
  const onAvailCheck = (checked, key) => {
    const buf = bufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    bufferRef.current = buf;
  };
  const onAddCols = () => {
    const next = Array.from(new Set([...selectedKeys, ...bufferRef.current]));
    setSelectedKeys(next);
  };
  const onRemove = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const onUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const onDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const onApplyCols = (e) => {
    e.preventDefault();
    // persist using the legacy key used by your page
    const payload = selectedKeys.map((k) => ({
      key: k,
      headerName: colMap.get(k)?.header || k,
    }));
    localStorage.setItem("OrderReportList", JSON.stringify(payload));
    closeCols();
  };

  // export helpers (Downloader lib expects CSV text — we’ll build it)
  const toCSV = (rowsForCsv) => {
    const cols = visibleCols;
    const head = cols.map((c) => c.header).join(",");
    const lines = rowsForCsv.map((r) =>
      cols
        .map((c) => {
          const val =
            typeof c.render === "function" ? (c.get ? c.get(r) : "") : c.get(r);
          const safe = toStr(val).replace(/"/g, '""');
          return `"${safe}"`;
        })
        .join(",")
    );
    return [head, ...lines].join("\r\n");
  };

  const exportPDF = async () => {
    await exportDataToPDF(toCSV(filteredRows), "TargetReportList");
  };
  const exportCSV = async () => {
    // Your Downloader lib has XLS/XLSX/XML helpers — CSV export was via AgGrid.
    // We’ll reuse the XLS helper for CSV-equivalent OR provide a quick data: URL fallback.
    const csv = toCSV(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TargetReportList.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const exportXLS = async () => {
    await convertDataCSVtoExcel(toCSV(filteredRows), "TargetReportList"); // legacy .XLS
  };
  const exportXLSX = async () => {
    await exportDataToExcel(toCSV(filteredRows), "TargetReportList");
  };
  const exportXML = async () => {
    await convertDataCsvToXml(toCSV(filteredRows), "TargetReportList");
  };

  // View Target action
  const onViewTarget = async (person) => {
    try {
      setLoading(true);
      const url = `${SalesPerson_targetReport + person?._id}/`;
      const res = await _Post(url, person?.database);
      setViewData({ data: person, achievements: res?.achievements });
      setViewOpen(true);
    } catch (err) {
      swal(
        `${err?.response?.data?.message || "Failed to load target"}`,
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleParentSubmit = (e) => {
    e.preventDefault();
    // if you need to refetch with another db, replicate your previous logic here
    // const [id, db] = JSON.parse(localStorage.getItem("SuperadminIdByMaster") || "").split(" ");
    // Apicalling(id, db) — we already fetch on mount; wire up here if needed.
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading…
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <a href="/#/dashboard" style={{ color: "green", paddingLeft: 10 }}>
        Home
      </a>{" "}
      / Lead
      <Card className="tw-card" style={{ marginTop: 10 }}>
        <Row style={{ margin: "6px" }}>
          <Col>
            <div className="tw-toolbar">
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Target Report{" "}
                <span className="count-pill">{filteredRows.length}</span>
              </div>

              {masterShow ? (
                <div style={{ flex: "1 1 240px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 120px" }} />
              )}

              <div style={{ minWidth: 260 }}>
                <Input
                  placeholder="search here…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-color"
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ height: 38 }}
                  />
                </div>
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ height: 38 }}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    /* filters already live-bind; button kept for UX */
                  }}
                  style={{
                    height: 38,
                    fontWeight: 600,
                    backgroundColor: "rgb(8, 91, 245)",
                  }}
                >
                  Submit
                </Button>
              </div>

              <Button
                outline
                color="primary"
                onClick={openCols}
                title="Filter Columns"
                style={{
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setIsOpenDl(true)}
                  onMouseLeave={() => setIsOpenDl(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="Download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {isOpenDl && (
                    <div className="dl-menu">
                      <div className="dl-item" onClick={exportPDF}>
                        .PDF
                      </div>
                      <div className="dl-item" onClick={exportCSV}>
                        .CSV
                      </div>
                      <div className="dl-item" onClick={exportXLS}>
                        .XLS
                      </div>
                      <div className="dl-item" onClick={exportXLSX}>
                        .XLSX
                      </div>
                      <div className="dl-item" onClick={exportXML}>
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>

        <CardBody style={{ marginTop: -10 }}>
          <div className="tw-table-wrap">
            <table className="tw-table">
              <thead>
                <tr>
                  {visibleCols.map((c) => (
                    <th key={c.key}>{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r?._id}>
                    {visibleCols.map((c) => (
                      <td key={c.key}>
                        {typeof c.render === "function"
                          ? c.render(r, onViewTarget)
                          : c.get(r)}
                      </td>
                    ))}
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
      {/* Column chooser modal */}
      <Modal isOpen={modalCols} toggle={closeCols} style={{ maxWidth: 1050 }}>
        <ModalHeader toggle={closeCols}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12">
              <h5>Available Columns</h5>
              <div className="picklist">
                {BASE_COLS.map((col) => (
                  <div key={col.key} className="pickitem">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          onAvailCheck(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={onAddCols}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="picklist" style={{ flex: 1 }}>
                  {selectedKeys.map((k, i) => {
                    const col = colMap.get(k);
                    return (
                      <div
                        key={k}
                        className="pickitem"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col?.header || k}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <BsFillArrowUpSquareFill
                    size={30}
                    className="arrowassign"
                    onClick={onUp}
                  />
                  <BsFillArrowDownSquareFill
                    size={30}
                    className="arrowassign"
                    onClick={onDown}
                  />
                  <Button size="sm" color="danger" onClick={onRemove}>
                    <IoMdRemoveCircleOutline size={18} className="mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Badge
                  color="primary"
                  style={{ cursor: "pointer" }}
                  onClick={onApplyCols}
                >
                  Submit
                </Badge>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
      {/* View target modal */}
      <Modal
        backdrop={false}
        fullscreen={true}
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1250 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          {viewData?.data?.firstName} Target
        </ModalHeader>
        <ModalBody>{viewData ? null : null}</ModalBody>
      </Modal>
    </>
  );
}
